import { useState } from "react";

import { api, ApiError } from "../api/client";
import { useAsync } from "../lib/useAsync";
import { ui } from "../lib/ui";
import { CopyButton } from "./CopyButton";
import { Panel } from "./Panel";

interface FootageRootPanelProps {
  /** Re-fetch when this changes (e.g. a day folder was created elsewhere). */
  version?: number;
  /** Called after this panel changed something on the server. */
  onChanged?: () => void;
}

/** The single shared footage directory. Recordings go in
 *  `<root>/<branch-id>/<YYYY-MM-DD>/`; this panel sets the root, creates it
 *  (plus one folder per camera branch) and copies its path. */
export function FootageRootPanel({ version = 0, onChanged }: FootageRootPanelProps) {
  const root = useAsync(() => api.footageRoot(), [version]);
  const ffmpeg = useAsync(() => api.footageFfmpeg(), []);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>, done: (count: number) => string) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = (await action()) as { created?: string[] } | undefined;
      setMessage(done(result?.created?.length ?? 0));
      onChanged?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = () => {
    setDraft(root.data?.path ?? "");
    setEditing(true);
    setError(null);
    setMessage(null);
  };

  const save = () =>
    run(
      async () => {
        await api.setFootageRoot(draft.trim() === "" ? null : draft.trim());
        setEditing(false);
      },
      () => "Footage root updated. Create the directories if they do not exist yet.",
    );

  const reset = () =>
    run(
      async () => {
        await api.setFootageRoot(null);
        setEditing(false);
      },
      () => "Footage root reset to the server default (FOOTFALL_FOOTAGE_ROOT).",
    );

  const create = () =>
    run(
      () => api.createFootageRoot(),
      (count) =>
        count === 0
          ? "Everything already exists — nothing to create."
          : `Created ${count} director${count === 1 ? "y" : "ies"}.`,
    );

  const data = root.data;

  return (
    <Panel
      title="Footage storage"
      subtitle="One shared directory on the worker host. Put each day's recordings in <root>/<branch-id>/<YYYY-MM-DD>/."
      actions={
        data && !editing ? (
          <div className="flex gap-1.5">
            <CopyButton text={data.path} />
            <button type="button" className={ui.btnQuiet} onClick={startEdit} disabled={busy}>
              Change
            </button>
            <button
              type="button"
              className={ui.btn}
              onClick={create}
              disabled={busy || (data.exists && data.branch_dirs_missing === 0)}
              title="Create the root and a folder for every branch with a camera"
            >
              Create directories
            </button>
          </div>
        ) : undefined
      }
    >
      {message && <p className={`${ui.notice} ${ui.noticeSuccess}`}>{message}</p>}
      {(error || root.error) && (
        <p className={`${ui.notice} ${ui.noticeError}`}>{error ?? root.error}</p>
      )}

      {!data && !root.error && <p className="text-muted">Loading…</p>}

      {data && !editing && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <code className="rounded-md bg-bg px-2.5 py-1.5 font-mono text-[13px] break-all">
            {data.path}
          </code>
          <span
            className={`${ui.pill} ${data.exists ? ui.pillSucceeded : ui.pillFailed}`}
          >
            {data.exists ? "exists" : "not created"}
          </span>
          {data.exists && data.branch_dirs_missing > 0 && (
            <span className="text-[13px] text-muted">
              {data.branch_dirs_missing} branch folder
              {data.branch_dirs_missing === 1 ? "" : "s"} missing
            </span>
          )}
          <span className="text-xs text-muted">
            {data.from_console ? "set from the console" : "server default (FOOTFALL_FOOTAGE_ROOT)"}
          </span>
        </div>
      )}

      {data && !editing && ffmpeg.data && (
        <p
          className="mt-3 text-xs text-muted"
          title={ffmpeg.data.path ?? undefined}
        >
          Native NVR exports (.dav, .264, .265) are converted before processing:{" "}
          {ffmpeg.data.available ? (
            <>
              ffmpeg {ffmpeg.data.version ?? "found"} on the worker host.
            </>
          ) : (
            <span className="text-danger">
              no ffmpeg on the worker host (install it or set FOOTFALL_FFMPEG) — drop
              MP4 exports until then.
            </span>
          )}
        </p>
      )}

      {data && editing && (
        <div className="flex flex-wrap items-end gap-3">
          <label className={`${ui.field} min-w-[360px] flex-1`}>
            Footage root (absolute path on the worker host)
            <input
              className={`${ui.input} font-mono text-xs`}
              value={draft}
              placeholder="/data/footage"
              autoFocus
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") setEditing(false);
              }}
            />
          </label>
          <button type="button" className={ui.btn} onClick={save} disabled={busy}>
            Save
          </button>
          {data.from_console && (
            <button type="button" className={ui.btnQuiet} onClick={reset} disabled={busy}>
              Reset to default
            </button>
          )}
          <button
            type="button"
            className={ui.btnQuiet}
            onClick={() => setEditing(false)}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      )}
    </Panel>
  );
}
